import React, { useEffect, useState, useCallback } from "react";
import { Siren, RefreshCw, MapPin, Phone, Check, CircleCheck, Loader2, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock, StatCard } from "../../components/app/ui";
import { adminApi, errorMessage, fmtDateTime, titleCase } from "../../lib/api";

const TABS = ["active", "acknowledged", "resolved"];
const roleLabel = (r) => ({ rider: "Customer", driver: "Driver", restaurant: "Restaurant" }[r] || r);
const statusLabel = (s) => (s === "active" ? "New" : titleCase(s));

export default function SosAlerts() {
  const [status, setStatus] = useState("active");
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback((silent) => {
    if (!silent) setData(null);
    adminApi.sosList(status).then((d) => { setData(d.alerts || []); setCounts(d.counts || {}); }).catch((e) => { toast.error(errorMessage(e)); setData([]); });
  }, [status]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 10000); // poll every 10s for new alerts
    return () => clearInterval(t);
  }, [load]);

  const ack = async (id) => {
    setBusy(id + "ack");
    try { await adminApi.ackSos(id); toast.success("Marked as acknowledged"); load(true); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };
  const resolve = async (id) => {
    setBusy(id + "resolve");
    try { await adminApi.resolveSos(id, ""); toast.success("SOS resolved"); load(true); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };

  const [commentText, setCommentText] = useState({});
  const [commentBusy, setCommentBusy] = useState(null);
  const addComment = async (id) => {
    const text = (commentText[id] || "").trim();
    if (!text) return;
    setCommentBusy(id);
    try {
      await adminApi.sosComment(id, text);
      setCommentText((p) => ({ ...p, [id]: "" }));
      toast.success("Comment added");
      load(true);
    } catch (e) { toast.error(errorMessage(e)); } finally { setCommentBusy(null); }
  };

  return (
    <>
      <PageHeader title="SOS alerts" description="Live emergency alerts from customers and drivers. Auto-refreshes every 10s." actions={<Button variant="outline" onClick={() => load()} className="rounded-full" data-testid="sos-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>} />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        {TABS.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`text-left ${status === s ? "ring-2 ring-brand rounded-2xl" : ""}`} data-testid={`sos-tab-${s}`}>
            <StatCard label={statusLabel(s)} value={counts[s] ?? "…"} icon={Siren} tone={s === "active" ? "brand" : "graphite"} />
          </button>
        ))}
      </div>
      {data === null && <LoadingBlock rows={4} />}
      {data && data.length === 0 && <EmptyState icon={Siren} title={`No ${status} alerts`} text="Nothing here right now." />}
      {data && data.length > 0 && (
        <div className="space-y-4" data-testid="sos-list">
          {data.map((a) => (
            <div key={a.sos_id} className={`kt-card p-5 ${a.status === "active" ? "border-2 border-red-500" : ""}`} data-testid={`sos-${a.sos_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                    <Siren className={`h-4 w-4 ${a.status === "active" ? "text-red-600" : "text-ink-muted"}`} />
                    {a.user_name} <span className="rounded-full bg-surface-100 px-2 py-0.5 text-xs text-ink-soft">{roleLabel(a.user_role)}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.status === "active" ? "bg-red-600 text-white" : a.status === "acknowledged" ? "bg-amber-500 text-white" : "bg-green-600 text-white"}`}>{statusLabel(a.status)}</span>
                    {a.status === "active" && a.tier_label && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700" data-testid={`sos-tier-${a.sos_id}`}>Tier: {a.tier_label}</span>}
                  </p>
                  {a.user_phone && <a href={`tel:${a.user_phone}`} className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline" data-testid={`sos-call-${a.sos_id}`}><Phone className="mb-0.5 h-3 w-3" /> {a.user_phone}</a>}
                  {a.context_label && <p className="text-sm text-ink-soft"><span className="text-ink-muted">On:</span> {a.context_label}</p>}
                  {a.note && <p className="rounded-xl bg-surface-100 p-3 text-sm text-ink-soft">{a.note}</p>}
                  <p className="pt-1 text-xs text-ink-muted">{fmtDateTime(a.created_at)}</p>
                  {a.acknowledged_by_name && <p className="text-xs text-emerald-700" data-testid={`sos-acktrail-${a.sos_id}`}>✓ Acknowledged by {a.acknowledged_by_name}{a.acknowledged_at ? ` · ${fmtDateTime(a.acknowledged_at)}` : ""}</p>}
                  {a.resolved_by_name && <p className="text-xs text-ink-muted">Resolved by {a.resolved_by_name}{a.resolved_at ? ` · ${fmtDateTime(a.resolved_at)}` : ""}{a.resolution_note ? ` — ${a.resolution_note}` : ""}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <a href={a.map_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700" data-testid={`sos-map-${a.sos_id}`}><MapPin className="h-3 w-3" /> Open location</a>
                  {a.status === "active" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => ack(a.sos_id)} disabled={!!busy} data-testid={`sos-ack-${a.sos_id}`}>{busy === a.sos_id + "ack" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Acknowledge</Button>}
                  {a.status !== "resolved" && <Button size="sm" className="rounded-full bg-green-600 text-white hover:bg-green-700" onClick={() => resolve(a.sos_id)} disabled={!!busy} data-testid={`sos-resolve-${a.sos_id}`}>{busy === a.sos_id + "resolve" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CircleCheck className="mr-2 h-4 w-4" />} Resolve</Button>}
                </div>
              </div>

              <div className="mt-4 border-t border-surface-200 pt-3" data-testid={`sos-comments-${a.sos_id}`}>
                {(a.escalations || []).length > 0 && (
                  <div className="mb-3" data-testid={`sos-escalations-${a.sos_id}`}>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-orange-600">Escalation log</p>
                    <ul className="space-y-1">
                      {a.escalations.map((e, i) => (
                        <li key={i} className="text-xs text-ink-soft" data-testid={`sos-escstep-${a.sos_id}-${i}`}>↗ {e.note || e.tier} · {fmtDateTime(e.at)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Action log</p>
                {(a.comments || []).length === 0 ? (
                  <p className="text-xs text-ink-muted">No comments yet. Record what happened and how the team responded.</p>
                ) : (
                  <ul className="space-y-2">
                    {a.comments.map((c) => (
                      <li key={c.comment_id} className="rounded-xl bg-surface-100 p-3" data-testid={`sos-comment-${c.comment_id}`}>
                        <p className="text-sm text-ink">{c.text}</p>
                        <p className="mt-1 text-[11px] text-ink-muted">{c.by_name || "Staff"}{c.by_role ? ` (${c.by_role})` : ""} · {fmtDateTime(c.at)}</p>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 flex gap-2">
                  <textarea
                    value={commentText[a.sos_id] || ""}
                    onChange={(e) => setCommentText((p) => ({ ...p, [a.sos_id]: e.target.value }))}
                    placeholder="Add a note on what happened / how we responded…"
                    rows={2}
                    className="flex-1 rounded-xl border border-surface-300 bg-white px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none"
                    data-testid={`sos-comment-input-${a.sos_id}`}
                  />
                  <Button size="sm" className="rounded-full bg-ink text-white hover:bg-ink/90 self-end" disabled={commentBusy === a.sos_id || !(commentText[a.sos_id] || "").trim()} onClick={() => addComment(a.sos_id)} data-testid={`sos-comment-add-${a.sos_id}`}>
                    {commentBusy === a.sos_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
